import {
  AlertDialog, AlertDialogTrigger, AlertDialogPortal, AlertDialogOverlay,
  AlertDialogContent, AlertDialogTitle, AlertDialogDescription,
  AlertDialogCancel, AlertDialogFooter
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NotificationRow } from "@/components/ui/notification-row";
import useMusioApi from "@/contexts/client/musio-client-context";
import { useIsMobile } from "@/hooks/use-mobile";
import { Edit } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

const ERROR_CHANGING_PASSWORD = "There was an error changing your password. Please check your inputs and try again later";
const SUCCESS_CHANGING_PASSWORD = "Success!";

interface FormState {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export const PasswordModal = () => {
  const { musioApi } = useMusioApi();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [formError, setFormError] = useState<string | null>(null);
  const isMobile = useIsMobile();

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setForm((prev) => ({ ...prev, [name]: value }));

      if (formError) setFormError(null);
    },
    [formError]
  );

  const { mutate, isError, isSuccess, isPending, error } = musioApi.useMutation("post", "/user/password");

  const isDisabled = useMemo(
    () => form.currentPassword.trim().length === 0 || isPending,
    [form.currentPassword, isPending]
  );

  useEffect(() => {
    if (isSuccess) {
      setForm(prev => {
        const cleared = Object.keys(prev).reduce((acc, key) => {
          acc[key as keyof FormState] = "";
          return acc;
        }, {} as FormState);
        return cleared;
      });
    }
  }, [isSuccess]);

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (form.newPassword !== form.confirmPassword) {
        setFormError("New passwords don't match");
        return;
      }

      mutate({
        body: {
          password: form.currentPassword,
          newPassword: form.newPassword,
        }
      });
    },
    [form, mutate]
  );

  const hasNotification = isSuccess || formError != null || isError;

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button className="p-2 px-5 bg-musio-mid-gray text-musio-light-lilac hover:text-musio-mid-gray">
          {isMobile ? <Edit /> : "Change Password"}
        </Button>
      </AlertDialogTrigger>

      <AlertDialogPortal>
        <AlertDialogOverlay className="AlertDialogOverlay" />
        <AlertDialogContent className="AlertDialogContent ">
          <form onSubmit={handleSubmit} className="min-h-0 flex flex-col gap-4">
            <AlertDialogTitle className="AlertDialogTitle flex items-center justify-between gap-4 p-8 bg-musio-bg">
              <div className="text-[30px] font-semibold text-musio-light-lilac">
                Change Password
              </div>
              <AlertDialogCancel asChild>
                <Button
                  type="button"
                  disabled={isSuccess}
                  className="select-none px-8 h-[38px] flex items-center justify-center rounded-full font-bold bg-musio-red text-musio-light-lilac cursor-pointer"
                >
                  <span className="text-sm">Go Back</span>
                </Button>
              </AlertDialogCancel>
            </AlertDialogTitle>

            <AlertDialogDescription className="AlertDialogDescription p-8 bg-musio-bg flex flex-col gap-2">
              {hasNotification ?
                <NotificationRow intent={(formError || isError) ? "error" : "success"}>
                  {isSuccess ? SUCCESS_CHANGING_PASSWORD : formError ? formError : error?.error?.passwordConstraint ? error.error.passwordConstraint : ERROR_CHANGING_PASSWORD}
                </NotificationRow> : null
              }

              <div className="flex flex-col gap-1 justify-baseline">
                <p className="text-musio-light-lilac text-lg">Current password</p>
                <Input
                  type="password"
                  id="currentPassword"
                  name="currentPassword"
                  value={form.currentPassword}
                  onChange={handleChange}
                  disabled={isSuccess}
                  required
                  autoComplete="current-password"
                />
              </div>

              <div className="flex flex-col gap-1 justify-baseline">
                <p className="text-musio-light-lilac text-lg">New password</p>
                <Input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  value={form.newPassword}
                  onChange={handleChange}
                  disabled={isSuccess}
                  required
                  autoComplete="new-password"
                />
              </div>

              <div className="flex flex-col gap-1 justify-baseline">
                <p className="text-musio-light-lilac text-lg">Confirm password</p>
                <Input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={form.confirmPassword}
                  onChange={handleChange}
                  disabled={isSuccess}
                  required
                  autoComplete="new-password"
                />
              </div>
            </AlertDialogDescription>

            <AlertDialogFooter className="p-8 flex flex-col gap-2 items-center justify-center bg-musio-bg">
              {isSuccess == false ?
                <Button
                  type="submit"
                  disabled={isDisabled}
                  className={`
                    select-none w-[404px] h-[35px] flex items-center justify-center
                    rounded-2xl font-bold text-musio-light-lilac cursor-pointer
                    bg-musio-green disabled:opacity-60
                  `}
                >
                  <p className="text-sm text-musio-mid-gray">
                    {isPending ? "Submitting…" : "Submit"}
                  </p>
                </Button>
                :
                <Button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    location.reload();
                  }}
                  className={`
                    select-none w-[404px] h-[35px] flex items-center justify-center
                    rounded-2xl font-bold text-musio-light-lilac cursor-pointer
                    bg-musio-green disabled:opacity-60
                  `}
                >
                  <p className="text-sm text-musio-mid-gray">
                    Close
                  </p>
                </Button>
              }
            </AlertDialogFooter>
          </form>
        </AlertDialogContent>
      </AlertDialogPortal>
    </AlertDialog>
  );
};

